import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { AccountActionModalsProps } from './types';

const AccountActionModals: React.FC<AccountActionModalsProps> = ({
  isDeleteModalOpen,
  isDeactivateModalOpen,
  isProcessing,
  onCloseDeleteModal,
  onCloseDeactivateModal,
  onConfirmDelete,
  onConfirmDeactivate
}) => {
  return (
    <>
      {/* Delete Account Modal */}
      <Transition.Root show={isDeleteModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={onCloseDeleteModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                  <div className="p-6">
                    <div className="flex items-center justify-center mb-6">
                      <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100">
                        <ExclamationTriangleIcon
                          className="h-6 w-6 text-red-600"
                          aria-hidden="true"
                        />
                      </div>
                    </div>

                    <Dialog.Title
                      as="h3"
                      className="text-lg font-semibold text-center text-gray-900 mb-4"
                    >
                      Delete Account Permanently
                    </Dialog.Title>

                    <p className="text-center text-gray-600 mb-4">
                      Are you absolutely sure you want to delete your account?
                      This action cannot be undone.
                    </p>

                    <div className="bg-red-50 border border-red-100 rounded-lg p-4 mb-6">
                      <p className="text-red-600 font-medium text-center mb-2">
                        The following will be permanently deleted:
                      </p>
                      <ul className="text-gray-600 space-y-2 text-center">
                        <li>Your profile and personal information</li>
                        <li>All your property listings</li>
                        <li>All messages and conversations</li>
                        <li>All saved preferences and settings</li>
                      </ul>
                    </div>

                    <div className="flex flex-col-reverse sm:flex-row sm:justify-center gap-3">
                      <button
                        type="button"
                        className="w-full sm:w-32 px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 font-medium focus:outline-none"
                        onClick={onCloseDeleteModal}
                        disabled={isProcessing}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="w-full sm:w-32 px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 font-medium focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={onConfirmDelete}
                        disabled={isProcessing}
                      >
                        {isProcessing ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Deactivate Account Modal */}
      <Transition.Root show={isDeactivateModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={onCloseDeactivateModal}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                  <div className="p-6">
                    <div className="flex items-center justify-center mb-6">
                      <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100">
                        <ExclamationTriangleIcon
                          className="h-6 w-6 text-yellow-600"
                          aria-hidden="true"
                        />
                      </div>
                    </div>

                    <Dialog.Title
                      as="h3"
                      className="text-lg font-semibold text-center text-gray-900 mb-4"
                    >
                      Deactivate Account
                    </Dialog.Title>

                    <p className="text-center text-gray-600 mb-6">
                      Your account will be deactivated and hidden from other
                      users. You can reactivate it by logging in again. All your
                      data will be preserved but not visible to others.
                    </p>

                    <div className="flex flex-col-reverse sm:flex-row sm:justify-center gap-3">
                      <button
                        type="button"
                        className="w-full sm:w-32 px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 font-medium focus:outline-none"
                        onClick={onCloseDeactivateModal}
                        disabled={isProcessing}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="w-full sm:w-32 px-4 py-2 rounded-lg bg-yellow-600 text-white hover:bg-yellow-700 font-medium focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={onConfirmDeactivate}
                        disabled={isProcessing}
                      >
                        {isProcessing ? 'Processing...' : 'Deactivate'}
                      </button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default AccountActionModals;
